import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from "react";
import { Box, Typography, Radio, IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SaveIcon from "@mui/icons-material/Save";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import AppButton from "../../../common_components/AppButton";
import { Dropdown, FormField } from "../../../common_components/FormInputs";
const FIELD_LABELS = {
    additional: "Additional",
    cc: "CC",
    bcc: "BCC",
};
const UserAssignmentDrawer = ({ open, onClose, allUsers, configurations, modules, onSave, saving = false, }) => {
    const [userId, setUserId] = useState("");
    const [copyFromUserId, setCopyFromUserId] = useState("");
    const [manualRoles, setManualRoles] = useState({});
    const [expandedKeys, setExpandedKeys] = useState(new Set());
    const sortedModules = useMemo(() => {
        const sortFn = (list) => [...list]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((m) => ({
            ...m,
            sub_modules: m.sub_modules ? sortFn(m.sub_modules) : [],
        }));
        return sortFn(modules);
    }, [modules]);
    useEffect(() => {
        if (open) {
            setUserId("");
            setCopyFromUserId("");
            setManualRoles({});
            setExpandedKeys(new Set());
        }
    }, [open]);
    const userOptions = useMemo(() => allUsers.map((u) => ({ value: u._id, label: `${u.first_name} ${u.last_name}` })), [allUsers]);
    const userOptionsForAssign = useMemo(() => userOptions.filter((o) => o.value !== copyFromUserId), [userOptions, copyFromUserId]);
    const copyFromOptions = useMemo(() => userOptions.filter((o) => o.value !== userId), [userOptions, userId]);
    const configsByModule = useMemo(() => {
        const grouped = {};
        configurations.forEach((c) => {
            const key = c.module_key || "";
            if (!key)
                return;
            if (!grouped[key])
                grouped[key] = [];
            grouped[key].push(c);
        });
        return grouped;
    }, [configurations]);
    const rolesForUser = (uid) => {
        const result = {};
        Object.entries(configsByModule).forEach(([moduleKey, configs]) => {
            const roles = configs
                .map((c) => {
                if (c.additional_user_ids?.some((u) => u._id === uid))
                    return "additional";
                if (c.cc?.some((u) => u._id === uid))
                    return "cc";
                if (c.bcc?.some((u) => u._id === uid))
                    return "bcc";
                return null;
            })
                .filter((r) => r !== null);
            const unique = [...new Set(roles)];
            if (unique.length === 1)
                result[moduleKey] = unique[0];
        });
        return result;
    };
    // User's current roles (so existing assignments are visible)
    const userRoles = useMemo(() => (userId ? rolesForUser(userId) : {}), [configsByModule, userId]);
    // Copy From overrides User's existing roles where it has a role
    const copyFromRoles = useMemo(() => (copyFromUserId ? rolesForUser(copyFromUserId) : {}), [configsByModule, copyFromUserId]);
    const effectiveRoles = useMemo(() => {
        const result = { ...userRoles, ...copyFromRoles };
        Object.entries(manualRoles).forEach(([mk, role]) => {
            result[mk] = role;
        });
        return result;
    }, [userRoles, copyFromRoles, manualRoles]);
    const setRole = (moduleKey, role) => {
        const current = effectiveRoles[moduleKey] ?? null;
        setManualRoles((prev) => ({
            ...prev,
            [moduleKey]: current === role ? null : role,
        }));
    };
    const getLeafKeys = (module) => {
        if (!module.sub_modules?.length)
            return [module.key];
        return module.sub_modules.flatMap(getLeafKeys);
    };
    const getParentRole = (module) => {
        const leaves = getLeafKeys(module);
        if (leaves.length === 0)
            return null;
        const first = effectiveRoles[leaves[0]] ?? null;
        if (first === null)
            return null;
        return leaves.every((k) => (effectiveRoles[k] ?? null) === first) ? first : null;
    };
    const setParentRole = (module, role) => {
        const leaves = getLeafKeys(module);
        const current = getParentRole(module);
        const target = current === role ? null : role;
        setManualRoles((prev) => {
            const next = { ...prev };
            leaves.forEach((k) => {
                next[k] = target;
            });
            return next;
        });
    };
    const toggleExpanded = (key) => {
        setExpandedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            return next;
        });
    };
    const assignedCount = Object.values(effectiveRoles).filter((v) => v !== null).length;
    const handleSave = async () => {
        if (!userId || assignedCount === 0)
            return;
        const updates = [];
        for (const [moduleKey, role] of Object.entries(effectiveRoles)) {
            const isExplicitlyCleared = moduleKey in manualRoles && manualRoles[moduleKey] === null;
            if (!role && !isExplicitlyCleared)
                continue;
            const configs = configsByModule[moduleKey] ?? [];
            for (const config of configs) {
                const additional = (config.additional_user_ids ?? []).map((u) => u._id).filter((id) => id !== userId);
                const cc = (config.cc ?? []).map((u) => u._id).filter((id) => id !== userId);
                const bcc = (config.bcc ?? []).map((u) => u._id).filter((id) => id !== userId);
                if (role === "additional")
                    additional.push(userId);
                else if (role === "cc")
                    cc.push(userId);
                else if (role === "bcc")
                    bcc.push(userId);
                updates.push({
                    configId: config._id,
                    additional_user_ids: additional,
                    cc,
                    bcc,
                    module_key: config.module_key ?? "",
                });
            }
        }
        if (updates.length > 0) {
            await onSave(updates);
        }
        onClose();
    };
    const headerActions = (_jsx(AppButton, { variant: "contained", color: "primary", size: "small", startIcon: _jsx(SaveIcon, { sx: { fontSize: "16px !important" } }), onClick: handleSave, disabled: saving || !userId || assignedCount === 0, className: "email-configuration-save-button", children: saving ? "Saving..." : "Save" }));
    const renderModule = (module, depth) => {
        const hasChildren = !!module.sub_modules?.length;
        const isExpanded = expandedKeys.has(module.key);
        const role = effectiveRoles[module.key] ?? null;
        const nameClassNames = [
            "email-configuration-module-tree-name",
            hasChildren ? "email-configuration-module-tree-name-parent" : "",
            depth ? "email-configuration-module-tree-name-indent" : "",
        ].filter(Boolean).join(" ");
        return (_jsxs(Box, { children: [_jsxs(Box, { className: "email-configuration-assign-row", children: [hasChildren ? (_jsx(IconButton, { size: "small", onClick: () => toggleExpanded(module.key), sx: { color: "#6B7280" }, children: isExpanded ? (_jsx(KeyboardArrowDownIcon, { sx: { fontSize: 18 } })) : (_jsx(KeyboardArrowRightIcon, { sx: { fontSize: 18 } })) })) : (_jsx(Box, { style: { width: 32 } })), _jsx(Typography, { className: nameClassNames, children: module.module_name }), hasChildren ? (["additional", "cc", "bcc"].map((fk) => {
                            const parentRole = getParentRole(module);
                            return (_jsx(Box, { className: "email-configuration-assign-radio-cell", children: _jsx(Radio, { checked: parentRole === fk, onClick: () => setParentRole(module, fk), size: "small", sx: {
                                        p: 0,
                                        color: "#D0D5DD",
                                        "&.Mui-checked": { color: "#FF008E" },
                                    } }) }, fk));
                        })) : (["additional", "cc", "bcc"].map((fk) => (_jsx(Box, { className: "email-configuration-assign-radio-cell", children: _jsx(Radio, { checked: role === fk, onClick: () => setRole(module.key, fk), size: "small", sx: {
                                    p: 0,
                                    color: "#D0D5DD",
                                    "&.Mui-checked": { color: "#FF008E" },
                                } }) }, fk))))] }), hasChildren && isExpanded && (_jsx(Box, { className: "email-configuration-module-tree-children", children: module.sub_modules?.map((child) => renderModule(child, depth + 1)) }))] }, module.key));
    };
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: _jsx(Typography, { className: "email-configuration-drawer-title", children: "Manage Recipients" }), headerActions: headerActions, width: "md", children: _jsx(Box, { className: "email-configuration-drawer-wrapper", children: _jsxs(Box, { className: "email-configuration-drawer-card email-configuration-drawer-card-flex", children: [_jsxs(Box, { className: "email-configuration-assign-top-row", children: [_jsx(FormField, { children: _jsx(Dropdown, { label: "User", value: userId, onChange: (v) => setUserId(v), options: userOptionsForAssign, placeholder: "Select user to assign...", searchable: true, required: true }) }), _jsx(FormField, { children: _jsx(Dropdown, { label: "Copy From", value: copyFromUserId, onChange: (v) => setCopyFromUserId(v), options: copyFromOptions, placeholder: "Select user to copy from...", searchable: true }) })] }), _jsxs(Box, { children: [_jsx(Typography, { className: "form-input-label", children: "Module" }), _jsxs(Box, { className: "email-configuration-assign-column-header", children: [_jsx(Box, {}), _jsx(Box, {}), ["additional", "cc", "bcc"].map((key) => (_jsx(Typography, { className: "email-configuration-assign-column-header-cell", children: FIELD_LABELS[key] }, key)))] }), _jsx(Box, { className: "email-configuration-assign-content", children: sortedModules.length > 0 ? (sortedModules.map((module) => (_jsx(Box, { children: renderModule(module, 0) }, module.key)))) : (_jsx(Typography, { className: "email-configuration-module-tree-empty", children: "No modules available" })) })] }), _jsx(Box, { className: "email-configuration-assign-footer", children: ["additional", "cc", "bcc"].map((key) => (_jsxs(Typography, { className: "email-configuration-assign-footer-text", children: [FIELD_LABELS[key], ":", " ", _jsx(Typography, { component: "span", className: "email-configuration-assign-footer-count", children: Object.values(effectiveRoles).filter((v) => v === key).length }), " ", "assigned"] }, key))) })] }) }) }));
};
export default UserAssignmentDrawer;
